// Shared Army tab labels, Font Awesome icons, and race-specific troop roster.

import unitsRoster from "@/data/units.json";

export const UNIT_SLUGS = [
  "defender",
  "scout",
  "melee",
  "ranged",
  "support",
];

export const UNIT_REQUIRES_NODE = {
  defender: "Barracks",
  melee: "MeleeHall",
  ranged: "RangedRange",
  scout: "ScoutOutpost",
  support: "Support",
};

export const NODE_LOCK_LABELS = {
  Barracks: "Barracks",
  MeleeHall: "Melee Hall",
  RangedRange: "Ranged Range",
  ScoutOutpost: "Scout Outpost",
  Support: "Support",
};

export const unitDisplayMap = {
  defender: { name: "Defender", icon: "fas fa-shield-alt", color: "#A9A9A9" },
  scout: { name: "Scout", icon: "fas fa-shoe-prints", color: "#87CEEB" },
  melee: { name: "Melee", icon: "fas fa-gavel", color: "#C45C26" },
  ranged: { name: "Ranged", icon: "fas fa-bullseye", color: "#7DFFA3" },
  support: { name: "Support", icon: "fas fa-plus", color: "#D8A0E8" },
};

export function recipeLineKey(line) {
  if (line?.recipe) return String(line.recipe).toLowerCase();
  if (line?.unit && UNIT_SLUGS.includes(String(line.unit).toLowerCase())) {
    return String(line.unit).toLowerCase();
  }
  return null;
}

export function unitDisplay(slug, raceSlug) {
  const key = String(slug || "").toLowerCase();
  const archetype = unitDisplayMap[key] || {
    name: key ? key.charAt(0).toUpperCase() + key.slice(1) : "Unit",
    icon: "fas fa-users",
    color: "#d8d0c0",
  };
  const raceKey = String(raceSlug || "").toLowerCase();
  const roster = unitsRoster?.[raceKey]?.[key];
  if (!roster) {
    return {
      ...archetype,
      description: "",
      hp: null,
      atk: null,
      def: null,
    };
  }
  return {
    ...archetype,
    name: roster.name || archetype.name,
    description: roster.description || "",
    hp: Number(roster.hp) || 0,
    atk: Number(roster.atk) || 0,
    def: Number(roster.def) || 0,
  };
}

export function cityNodeDisplayName(nodeKey, raceLabelKey, nodes = []) {
  if (!nodeKey) return "";
  const node = (Array.isArray(nodes) ? nodes : []).find(
    (entry) => (entry.node_key || entry.id) === nodeKey
  );
  const raced = raceLabelKey ? node?.labels?.[raceLabelKey]?.label : null;
  if (raced) return raced;
  return NODE_LOCK_LABELS[nodeKey] || nodeKey;
}

export function unitBuildingLockState(slug, { unlockedNodes, nodeLabels } = {}) {
  const key = String(slug || "").toLowerCase();
  const reasons = [];
  const unlocked = new Set(
    (Array.isArray(unlockedNodes) ? unlockedNodes : []).map((node) => String(node))
  );
  const barracksLabel = nodeLabels?.Barracks || NODE_LOCK_LABELS.Barracks;
  if (!unlocked.has("Barracks")) {
    reasons.push(`Requires ${barracksLabel}`);
    return { locked: true, reasons };
  }
  const nodeKey = UNIT_REQUIRES_NODE[key];
  if (nodeKey && !unlocked.has(nodeKey)) {
    reasons.push(
      `Requires ${nodeLabels?.[nodeKey] || NODE_LOCK_LABELS[nodeKey] || nodeKey}`
    );
  }
  return { locked: reasons.length > 0, reasons };
}

export function unitLockState(slug, { unlockedNodes } = {}) {
  const key = String(slug || "").toLowerCase();
  const reasons = [];
  const nodeKey = UNIT_REQUIRES_NODE[key];
  const unlocked = new Set(
    (Array.isArray(unlockedNodes) ? unlockedNodes : []).map((node) => String(node))
  );
  if (nodeKey && !unlocked.has(nodeKey)) {
    reasons.push(`Requires ${NODE_LOCK_LABELS[nodeKey] || nodeKey}`);
  }
  return { locked: reasons.length > 0, reasons };
}

export function emptyUnitMap() {
  return Object.fromEntries(UNIT_SLUGS.map((slug) => [slug, 0]));
}

export function normalizeUnitMap(source) {
  const units = emptyUnitMap();
  if (!source || typeof source !== "object") return units;
  for (const [slug, amount] of Object.entries(source)) {
    if (!slug) continue;
    units[slug] = Math.max(0, Math.floor(Number(amount) || 0));
  }
  return units;
}
